import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ICustomer } from '../interfaces/customer';
import { ICustomerFilter } from '../interfaces/customer-filter';
import { ICustomerPhotos } from '../interfaces/customer-photos';
import { EditService } from './edit.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { ICustomerPremiumTimeBand } from '../interfaces/customer-premium-timeband';
import { IOrganisationSpecificStepGroup } from '../interfaces/organisation-specific-steps';
import { Subject } from 'rxjs';
import { ICustomerProfileDetail } from '../interfaces/telephony/customer/customer-profile-detail.interface';
import { ICustomerProfileActivity } from '../interfaces/telephony/customer/customer-profile-activity.interface';
import { IProfileJobInfo } from '../interfaces/telephony/profile-job-info';
import { IGetProfileOrdersFilter } from '../interfaces/telephony/customer/get-profile-orders-filter.interface';
import { ICustomerUserInfo } from '../interfaces/telephony/customer/customer-user-info.interface';
import { ICustomerStructureData } from '../interfaces/telephony/customer/customer-structure-data.dto.interface';
import { IUpdateOrgActivityStatus } from '../interfaces/update-org-activity-status.interface';
import { UserAutoDeactivationAuditDTO } from '../interfaces/customer-user-deactivation/user-auto-deactivation-audit.dto.interface';
import { IOrganisationSubtype } from '../interfaces/customer-user-deactivation/organisation-subtype.dto.interface';
import moment from 'moment/moment';
import { Message } from '../classes/Message';
import FileSaver from 'file-saver';
import { MessageService } from 'primeng';
import { OrganisationNameAndLogoDTO } from '../interfaces/organisation-name-and-logo';

@Injectable({ providedIn: 'root' })
export class CustomerService extends EditService<ICustomer, ICustomerFilter> {
  url = '/api/customers';
  entityTitle = '';

  private selected_org_id = new Subject<number>();
  dataNumber$ = this.selected_org_id.asObservable();

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    private messageService: MessageService,
  ) {
    super(http, handlersFactory);
  }

  setSelectedOrgId(id: number) {
    this.selected_org_id.next(id);
  }

  getPhotos(id?: number): Promise<ICustomerPhotos> {
    let url = `${this.url}/photos`;
    if (id) {
      url += `/${id}`;
    }
    return this.handleRequest<ICustomerPhotos>(
      this.http.get<ICustomerPhotos>(url),
      this.handlersFactory.getForReadById(this.entityTitle),
    );
  }

  savePhotos(data: ICustomerPhotos): Promise<ICustomerPhotos> {
    return this.handleRequest<ICustomerPhotos>(
      this.http.post<ICustomerPhotos>(`${this.url}/photos`, data),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Information Saved',
    );
  }

  GetCurrent(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/customers/getcurrent'),
      this.handlersFactory.getForReadAll(),
    );
  }

  saveCustomerForm(data: any, isNew: boolean, isVme?: boolean): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    let successMsg = isNew ? 'Organisation added.' : 'Organisation updated.';

    if (isVme) successMsg = 'Information saved.';

    return this.handleRequest(
      this.http.post('api/customers/saveform', data, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  getTypes(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/gettypes'),
      this.handlersFactory.getForReadAll(),
    );
  }

  getSubTypes(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/getsubtypes'),
      this.handlersFactory.getForReadAll(),
    );
  }

  getContractTypes(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/getContractTypes'),
      this.handlersFactory.getForReadAll(),
    );
  }

  updateActivityStatus(item: IUpdateOrgActivityStatus): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    let successMsg = item.deactivate ? 'Organisation deactivated.' : 'Organisation activated.';
    if (item.suspend) {
      successMsg = 'Organisation Suspended';
    }

    return this.handleRequest(
      this.http.post(`${this.url}/updateActivityStatus`, item, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  getSelectList(filterCommand: ICustomerFilter): Promise<any> {
    return this.handleRequest(
      this.http.get<any>('api/customers/selectlist', {
        params: this.constructHttpParams(filterCommand),
      }),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  getPremiumTimeBands(customerId: number): Promise<ICustomerPremiumTimeBand[]> {
    if (!customerId) {
      return this.handleRequest(
        this.http.get<any>(`${this.url}/getGenericPremiumTimeBands`),
        this.handlersFactory.getForReadAll(),
      ).then((result) => result);
    } else {
      return this.handleRequest(
        this.http.get<any>(`${this.url}/getPremiumTimeBands/${customerId}`),
        this.handlersFactory.getForReadAll(),
      ).then((result) => result);
    }
  }

  addPremiumTimeBand(timeBandDTO: ICustomerPremiumTimeBand): Promise<any> {
    return this.handleRequest(
      this.http.post<any>(`${this.url}/addPremiumTimeBand`, timeBandDTO),
      this.handlersFactory.getForCreate('Premium Time-band'),
    ).then((result) => result);
  }

  updatePremiumTimeBand(timeBandDTO: ICustomerPremiumTimeBand): Promise<any> {
    return this.handleRequest(
      this.http.put<any>(`${this.url}/updatePremiumTimeBand`, timeBandDTO),
      this.handlersFactory.getForUpdate('Premium Time-band'),
    ).then((result) => result);
  }

  deletePremiumTimeBand(id: number): Promise<any> {
    return this.handleRequest(
      this.http.delete<any>(`${this.url}/deletePremiumTimeBand/${id}`),
      this.handlersFactory.getForDelete('Premium Time-band'),
    ).then((result) => result);
  }

  getDepartments(customerId: number): Promise<any> {
    console.log('Getting from url: ', `${this.url}/getDepartments/${customerId}`);
    return this.http.get<any>(`${this.url}/getDepartments/${customerId}`).toPromise();
  }

  getStructureData(customerId: number): Promise<ICustomerStructureData> {
    return this.http
      .get<ICustomerStructureData>(`${this.url}/${customerId}/get-structure-data`)
      .toPromise();
  }

  loadChecklistCustomers(selectedChecklist: IOrganisationSpecificStepGroup): Promise<ICustomer[]> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/getChecklistCustomers/${selectedChecklist.id}`),
      this.handlersFactory.getForReadAll(),
    ).then((result) => result);
  }

  getProfileDetail(customerId: number): Promise<ICustomerProfileDetail> {
    return this.http
      .get<ICustomerProfileDetail>(`${this.url}/${customerId}/profile-detail`)
      .toPromise();
  }

  getProfileActivity(customerId: number): Promise<ICustomerProfileActivity> {
    return this.http
      .get<ICustomerProfileActivity>(`${this.url}/${customerId}/profile-activity`)
      .toPromise();
  }

  getProfileOrders(filter: IGetProfileOrdersFilter): Promise<IProfileJobInfo[]> {
    const query = encodeURIComponent(JSON.stringify(filter));
    return this.http
      .get<IProfileJobInfo[]>(`${this.url}/${filter.customerId}/profile-orders?query=${query}`)
      .toPromise();
  }

  getCallStats(customerId: number): Promise<TelephonyStatDTO[]> {
    return this.http.get<any[]>(`${this.url}/${customerId}/call-stats`).toPromise();
  }

  getUsersInfo(customerId: number): Promise<ICustomerUserInfo[]> {
    return this.http.get<ICustomerUserInfo[]>(`${this.url}/${customerId}/users-info`).toPromise();
  }

  patch(customerId: number, patch: any): Promise<any> {
    return this.http.patch(`${this.url}/${customerId}`, patch).toPromise();
  }

  getCompanyInfoURL(customerId: number): Promise<any> {
    return this.http.get<any>(`${this.url}/${customerId}/company-info-url`).toPromise();
  }

  getDeactivatedUsers(): Promise<UserAutoDeactivationAuditDTO[]> {
    return this.handleRequest(
      this.http.get<UserAutoDeactivationAuditDTO[]>(`${this.url}/deactivated-users`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getWarnedUsers(): Promise<UserAutoDeactivationAuditDTO[]> {
    return this.handleRequest(
      this.http.get<UserAutoDeactivationAuditDTO[]>(`${this.url}/warned-users`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getDeactivationSettings(): Promise<IOrganisationSubtype[]> {
    return this.handleRequest(
      this.http.get<IOrganisationSubtype[]>(`${this.url}/deactivation-settings`),
      this.handlersFactory.getForReadAll(),
    );
  }

  saveDeactivationSettings(data: any): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json;charset=utf-8' });

    return this.handleRequest(
      this.http.post(this.url + '/deactivation-settings', data, { headers: headers }),
      this.handlersFactory.getForUpdate(this.entityTitle),
      'Supplier Settings Saved',
    );
  }

  generateCustomerUserCRMCSV() {
    let filename = `Customer_User_CRM_Sync_Data_${moment().format('YYYY-MM-DD hh:mm')}.csv`;

    let url = `${this.url}/customer_user_crm_sync_data`;

    let report = this.handleRequest(
      this.http.get(url, { responseType: 'blob' }),
      this.handlersFactory.getForReadAll(),
    ).then((result) => {
      FileSaver.saveAs(result, filename);

      this.messageService.add(
        Message.Success('Downloaded successful. Check your download folder.'),
      );
    });

    return report;
  }

  getOrganisationNameAndLogo(): Promise<OrganisationNameAndLogoDTO> {
    return this.handleRequest(
      this.http.get<OrganisationNameAndLogoDTO>('api/customers/getOrgNameAndLogo'),
      this.handlersFactory.getForReadAll(),
    );
  }
}
